import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1, selectedColor = '') => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(
        item => item._id === product._id && (item.selectedColor || '') === (selectedColor || '')
      );

      if (existingItem) {
        return prevItems.map(item =>
          item._id === product._id && (item.selectedColor || '') === (selectedColor || '')
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...prevItems, { ...product, quantity, selectedColor: selectedColor || '' }];
    });
  };

  const removeFromCart = (productId, selectedColor = '') => {
    setCartItems(prevItems =>
      prevItems.filter(item => !(item._id === productId && (item.selectedColor || '') === (selectedColor || '')))
    );
  };

  const updateQuantity = (productId, quantity, selectedColor = '') => {
    if (quantity <= 0) {
      removeFromCart(productId, selectedColor);
      return;
    }
    
    setCartItems(prevItems =>
      prevItems.map(item =>
        item._id === productId && (item.selectedColor || '') === (selectedColor || '')
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
